import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import PeopleIcon from '@material-ui/icons/People';
import AssessmentIcon from '@material-ui/icons/Assessment'; // For Power BI
import Description from '@material-ui/icons/Description'; // For EIR Matrix

import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import MenuIcon from '@material-ui/icons/Menu';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BuildIcon from '@material-ui/icons/Build';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { PowerBIReportPage } from '../PowerBI/PowerBIReportPage';
import { NewReleasesOutlined, VerifiedUser } from '@material-ui/icons';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 2.6 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
  },
});

// Allow sidebar item labels to wrap to multiple lines instead of being
// truncated with an ellipsis, while keeping the sidebar width unchanged.
const useSidebarWrapStyles = makeStyles({
  '@global': {
    '[class*="BackstageSidebarItem-root"]': {
      height: 'auto !important',
      minHeight: '44px !important',
      paddingTop: '6px !important',
      paddingBottom: '6px !important',
      paddingLeft: '12px !important',
      alignItems: 'center',
    },
    '[class*="BackstageSidebarItem-iconContainer"]': {
      flex: '0 0 auto !important',
      minWidth: '40px !important',
      width: '40px !important',
      marginRight: '0 !important',
    },
    '[class*="BackstageSidebarItem-label"]': {
      whiteSpace: 'normal !important',
      overflow: 'visible !important',
      textOverflow: 'clip !important',
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      width: 'auto !important',
      minWidth: '0 !important',
      flex: '1 1 auto !important',
      textAlign: 'left',
      margin: '0 !important',
      paddingLeft: '2px',
      paddingRight: '8px',
      lineHeight: '1.2 !important',
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link 
        to="/" 
        underline="none" 
        className={classes.link} 
        aria-label="Home-logo"
        style={{ marginLeft: isOpen ? 28 : 15 }}
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const SidebarToggle = () => {
  const { isOpen, setOpen } = useSidebarOpenState();
  return (
    <IconButton onClick={() => setOpen(!isOpen)} aria-label="Toggle sidebar" style={{color: 'white'}}>
      {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
    </IconButton>
  );
}

export const Root = ({ children }: PropsWithChildren<{}>) => {
  useSidebarWrapStyles();
  const location = useLocation();
  const isOnPowerBI = location.pathname === '/powerbi';

  const utilityPaths = [
    '/request-access',
    '/track-requests',
    '/token-generator',
    '/token-validator',
  ];
  const isOnUtility = utilityPaths.some(
    p => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  // Keep the dropdown open while the user is on one of the utility routes.
  const [utilitiesOpen, setUtilitiesOpen] = useState(isOnUtility);

  // Root is a persistent layout, so navigating into a utility route after mount
  // won't re-run the initializer above; open the menu when that happens.
  useEffect(() => {
    if (isOnUtility) {
      setUtilitiesOpen(true);
    }
  }, [isOnUtility]);

  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Service Catalog';

  // Keep the browser tab title in sync with the current route. Entity detail
  // pages (/catalog/:namespace/:kind/:name) manage their own document title
  // via Backstage (the API name), so those are intentionally skipped here.
  useEffect(() => {
    const path = location.pathname;
    const isEntityPage = /^\/catalog\/[^/]+\/[^/]+\/[^/]+/.test(path);
    if (isEntityPage) {
      return;
    }

    const routeTitles: { path: string; title: string }[] = [
      { path: '/powerbi', title: 'Dashboard' },
      { path: '/catalog', title: 'Service Details' },
      { path: '/capabilities', title: 'Service Capabilities' },
      { path: '/eir-matrix', title: 'Entitlement Compliance Matrix' },
      { path: '/onboarding', title: 'Onboarding Guide' },
      { path: '/request-access', title: 'Request Access Form' },
      { path: '/track-requests', title: 'Track Adoption Request' },
      { path: '/token-generator', title: 'Token Generator' },
      { path: '/token-validator', title: 'Token Validator' },
      { path: '/release-notes', title: 'Release Notes' },
      { path: '/settings', title: 'Settings' },
      { path: '/api-docs', title: 'API Docs' },
      { path: '/catalog-graph', title: 'Catalog Graph' },
      { path: '/catalog-import', title: 'Register an Entity' },
    ];

    const match = routeTitles.find(
      r => path === r.path || path.startsWith(`${r.path}/`),
    );

    document.title = match ? `${match.title} | ${appTitle}` : appTitle;
  }, [location.pathname, appTitle]);


useEffect(() => {
  const ariaLabelToTitleMap: { [key: string]: string } = {
    'Home':'Home',
    'Consumers and Service/APIs Selected':'Consumers and Service/APIs Selected',
    'Consumer Details':'Consumer Details', 
    'Catalog':'Catalog',
    'Dashboard':'PowerBI Dashboard',
    'Service Details':'Service Details',
    'Service Capabilities':'Service Capabilities',
    'Entitlement Compliance Matrix':'Entitlement Compliance Matrix',
    'Team':'Team',
    'Example':'Example',
    'Onboarding Guide':'Onboarding Guide',
    'Request Access Form':'Request Access Form',
    'Track Adoption Request':'Track Adoption Request',
    'Token Generator':'Token Generator',
    'Token Validator':'Token Validator',
    'Release Notes':'Release Notes',
    'Settings':'Settings and Release Notes',
};

Object.keys(ariaLabelToTitleMap).forEach(ariaLabel => {
  const element = document.querySelector(`[aria-label="${ariaLabel}"]`);
  if (element) {
    if (element.textContent) {
          element.setAttribute('title', ariaLabelToTitleMap[ariaLabel]);
  }}
  });
  }, []);

  return (
  <SidebarPage>
    <Sidebar disableExpandOnHover>
      <SidebarLogo />
      <SidebarDivider />

      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={AssessmentIcon} to="powerbi" text="Dashboard" />
        <SidebarItem icon={ReceiptLongIcon} to="catalog?filters%5Bkind%5D=API&limit=500" text="Service Details" />
        <SidebarItem icon={Description} to="capabilities" text="Service Capabilities" />
        <SidebarItem icon={VerifiedUser} to="eir-matrix" text="Entitlement Compliance Matrix" />
        <SidebarItem icon={PeopleIcon} to="onboarding" text="Onboarding Guide" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>

      <SidebarSpace />
      <SidebarDivider />
      <SidebarItem
        icon={BuildIcon}
        text="Utilities"
        onClick={() => setUtilitiesOpen(open => !open)}
      >
        {utilitiesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </SidebarItem>
      {utilitiesOpen && (
        <div style={{ borderLeft: '2px solid rgba(255,255,255,0.12)', marginLeft: 12 }}>
          <SidebarItem icon={AssignmentIcon} to="/request-access" text="Request Access Form" />
          <SidebarItem icon={TrackChangesIcon} to="/track-requests" text="Track Adoption Request" />
          <SidebarItem icon={VpnKeyIcon} to="/token-generator" text="Token Generator" />
          <SidebarItem icon={VerifiedUserIcon} to="/token-validator" text="Token Validator" />
        </div>
      )}
      <SidebarItem icon={NewReleasesOutlined} to="/release-notes" text="Release Notes" />

      <SidebarItem icon={SettingsSuggestIcon} to="/settings" text="Settings" /> 

      <SidebarToggle/>
    </Sidebar>
    <PowerBIReportPage visible={isOnPowerBI} />
    <div style={{ display: isOnPowerBI ? 'none' : 'contents' }}>
      {children}
    </div>
  </SidebarPage>
)};
